import { Col, Dropdown, Row } from '@everlywell/leaves';
import { validateZipCode } from '@everlywell/leaves/lib/shared/formUtils';
import { stateAbbreviationToNames } from '@everlywell/leaves/lib/shared/states';
import { TrackVisibilityWrapper } from 'components/TrackVisibilityWrapper';
import React, { SyntheticEvent, useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import analytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import { logError } from 'utils/helpers';
import { UserSettingsContext } from 'utils/hooks/useAccountSettings/context';
import { ShippingSettings as ShippingSettingsType } from 'utils/hooks/useAccountSettings/types';
import { useInteractionTracker } from 'utils/hooks/useInteractionTracker';
import { UserContext } from 'utils/hooks/useUserContext/context';
import { shippableStates, stateNameToAbbreviation } from './constants';
import CONTENT from './content';
import * as S from './styles';
import { ButtonSpinner, ButtonText, LoadingSpinner } from './styles';
import { zipcodeCleaningValidation } from './validations';

type Props = {
  smallSave?: boolean;
  children?: any;
};

export const TEST_ID = 'ShippingSettingsTest';

export const ShippingSettings = ({ smallSave, children }: Props) => {
  const {
    loadingShippingInfo,
    savingShippingInfo,
    shippingAddress,
    invalidAddress,
    updateShippingAddress,
  } = useContext(UserSettingsContext);

  const {
    userData: { activeCredits, isMember, userEmail },
  } = useContext(UserContext);
  const trackFormInteraction = useInteractionTracker();
  const { reset, register, errors, setValue, handleSubmit, formState } =
    useForm();

  const [_editing, _setEditing] = useState(false);

  const [disabled, setDisabled] = useState(true);
  const [NYError, setNYError] = useState(false);

  const loadInitialState = async () => {
    setValue('firstName', shippingAddress?.first_name);
    setValue('lastName', shippingAddress?.last_name);
    setValue('address1', shippingAddress?.address1);
    setValue('address2', shippingAddress?.address2);
    setValue('city', shippingAddress?.city);
    shippingAddress?.state &&
      setValue('state', stateAbbreviationToNames[shippingAddress.state]);
    setValue('zip_code', shippingAddress?.zipcode);
  };

  const updateShippingInformation = async (formData: any) => {
    try {
      const newSettings: ShippingSettingsType = {
        first_name: shippingAddress?.first_name || '',
        last_name: shippingAddress?.last_name || '',
        address1: formData.address1,
        address2: formData.address2,
        state: stateNameToAbbreviation[formData.state],
        city: formData.city,
        zipcode: formData.zip_code,
      };
      await updateShippingAddress(newSettings);
      reset(formData);
      _setEditing(false);
    } catch (err) {
      logError(`There was an issue updating the shipping information.`, {
        component: 'ShippingSettings',
        function: 'updateShippingInformation',
        stackTrace: err,
      });
    }
  };

  const onStateChange = (e: any) => setNYError(e.target.value === 'New York');

  useEffect(() => {
    if (_editing && shippingAddress)
      loadInitialState().then(() => setDisabled(false));
  }, [_editing]);

  useEffect(() => {
    if (shippingAddress && !shippingAddress.city && !shippingAddress.address1) {
      _setEditing(true);
    }
  }, [shippingAddress]);

  const handleFocus = async () => {
    await trackFormInteraction({
      event: ANALYTICS.EVENTS.FORM_INTERACTION,
      data: {
        component: ANALYTICS.LABELS.ORDER_MODAL,
        activeCredits,
      },
    });
  };

  const submissionDisabled =
    !formState.isDirty ||
    disabled ||
    savingShippingInfo ||
    loadingShippingInfo ||
    NYError;

  const onSubmit = async (e: any) => {
    try {
      if (!submissionDisabled) {
        await updateShippingInformation(e);
      } else {
        await analytics.track({
          event: ANALYTICS.EVENTS.CLICKED_BUTTON,
          data: {
            label: ANALYTICS.LABELS.ORDER_MODAL_CANCEL.label,
            description: ANALYTICS.LABELS.ORDER_MODAL_CANCEL.description,
            isMember,
          },
        });
        _setEditing(false);
      }
    } catch (e) {
      logError(
        `There was an issue submitting the shipping information form at Membership Credit Redemption Modal.`,
        {
          component: 'ShippingSettings',
          function: 'onSubmit',
          stackTrace: e,
        },
      );
    }
  };

  const onEdit = async () => {
    await analytics.track({
      event: ANALYTICS.EVENTS.CLICKED_BUTTON,
      data: {
        label: ANALYTICS.LABELS.ORDER_MODAL_EDIT.label,
        description: ANALYTICS.LABELS.ORDER_MODAL_EDIT.description,
        isMember,
      },
    });
    _setEditing(!_editing);
  };

  const comp = _editing ? (
    <S.Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        {smallSave ? (
          <S.Header>
            <S.SubtitleShort>{CONTENT.titleShort}</S.SubtitleShort>
            <S.SmallEdit
              appearance="primary"
              // @ts-ignore
              className={savingShippingInfo ? 'loading' : ''}
              type="submit"
            >
              <ButtonSpinner saving={savingShippingInfo} />

              <ButtonText saving={savingShippingInfo}>
                {submissionDisabled
                  ? CONTENT.buttonCancel
                  : CONTENT.buttonShort}
              </ButtonText>
            </S.SmallEdit>
          </S.Header>
        ) : (
          <>
            {CONTENT.title && <S.Title>{CONTENT.title}</S.Title>}
            {CONTENT.subtitle && <S.SubTitle>{CONTENT.subtitle}</S.SubTitle>}
          </>
        )}

        <Row>
          <S.HiddenInput
            name="firstName"
            id="firstName"
            aria-hidden="true"
            ref={register}
          />
          <S.HiddenInput
            name="lastName"
            id="lastName"
            aria-hidden="true"
            ref={register}
          />
          <Col xs={12} md={6}>
            <S.Input
              id="ss-address1"
              name="address1"
              label="Address"
              placeholder="Address"
              ref={register({ required: 'This field is required.' })}
              disabled={disabled}
              error={errors.address1 && errors.address1.message}
              autoComplete="shipping address-line1"
              onFocus={handleFocus}
            />
          </Col>
          <Col xs={12} md={6}>
            <S.Input
              id="ss-address2"
              name="address2"
              label="Address Line 2 (Optional)"
              placeholder="Apt/Unit/Suite"
              ref={register}
              disabled={disabled}
              error={errors.address2 && errors.address2.message}
              autoComplete="shipping address-line2"
              onFocus={handleFocus}
            />
          </Col>
        </Row>
        <Row>
          <Col xs={12} md={6}>
            <S.Input
              id="ss-city"
              name="city"
              label="City"
              placeholder="City"
              ref={register({ required: 'This field is required.' })}
              disabled={disabled}
              error={errors.city && errors.city.message}
              autoComplete="shipping locality"
              onFocus={handleFocus}
            />
          </Col>
          <Col xs={6} md={3}>
            <Dropdown
              // @ts-ignore
              items={shippableStates} // States data does not currently include New York
              label="State"
              name="state"
              id="ss-state"
              placeholderText="State"
              showErrorMessage
              error={
                (errors.state && errors.state.message) ||
                (NYError &&
                  'For regulatory reasons, our tests are not available in NY with the exception of COVID-19.')
              }
              ref={register({
                required: 'Please select a state',
              })}
              autoComplete="shipping region"
              onClick={handleFocus}
              onChange={onStateChange}
            />
          </Col>
          <Col xs={6} md={3}>
            <S.Input
              id="ss-zip_code"
              name="zip_code"
              label="ZIP Code"
              placeholder="00000"
              // @ts-ignore
              inputmode="numeric"
              pattern="\d*"
              type="text"
              ref={register({
                required: 'Please enter a ZIP code',
                validate: (value) =>
                  validateZipCode(value) || 'Please enter a valid ZIP code.',
              })}
              disabled={disabled}
              error={errors.zip_code && errors.zip_code.message}
              autoComplete="shipping postal-code"
              onFocus={handleFocus}
              onChange={(e: SyntheticEvent<HTMLInputElement>) => {
                const target = e.target as HTMLInputElement;
                const zipcode = zipcodeCleaningValidation(target.value);
                target.value = zipcode;
              }}
            />
          </Col>
        </Row>
        {invalidAddress && (
          <S.InvalidAddress>Address is invalid</S.InvalidAddress>
        )}
        {!smallSave && (
          <S.ButtonRow>
            <S.Button
              appearance="primary"
              isDisabled={submissionDisabled}
              // @ts-ignore
              className={savingShippingInfo ? 'loading' : ''}
            >
              <ButtonSpinner saving={savingShippingInfo} />

              <ButtonText saving={savingShippingInfo}>
                {CONTENT.button}
              </ButtonText>
            </S.Button>
          </S.ButtonRow>
        )}
      </form>
    </S.Box>
  ) : (
    <S.Box>
      {smallSave && <S.SubtitleShort>Shipping Address</S.SubtitleShort>}
      <S.Edit onClick={onEdit}>{CONTENT.buttonShortEdit}</S.Edit>

      <S.AddressContainer>
        {loadingShippingInfo && <LoadingSpinner />}
        <S.AddressLine>
          {shippingAddress?.address1} {shippingAddress?.address2} <br />
          {shippingAddress?.city}, {shippingAddress?.state}{' '}
          {shippingAddress?.zipcode} <br />
          {userEmail} <br />
        </S.AddressLine>
      </S.AddressContainer>
    </S.Box>
  );

  return (
    <TrackVisibilityWrapper label={ANALYTICS.LABELS.ORDER_MODAL}>
      <S.Container data-test={TEST_ID}>{comp}</S.Container>
      {children && children(_editing)}
    </TrackVisibilityWrapper>
  );
};

export default ShippingSettings;
